/**
 * AttendanceSummaryReportPage — the Attendance Summary generator
 * (PRD_AttendanceSummaryReport C3/C3a/C4/C5/C13/C14/C15/C18).
 *
 * Same control surface as DtrReportPage — preset chips, a FROM/TO pair, the
 * three-way scope selector — with one difference: the range rule is a WINDOW
 * (this month + last month, C3), not the DTR's 31-day span, so validation and
 * presets are this page's own exported pure functions of an injected today
 * (C4) rather than DtrReportPage's.
 *
 * Generate-then-download (C13) started here and now belongs to both reports —
 * the lifecycle lives in usePdfPreview + PdfPreviewPanel (C22).
 *
 * Reads are DtrReportPage's, copied: one roster query, then one getDoc per
 * employee per month document (1-2 under C3), nested-parallel. A rejected
 * read aborts the whole generate — never a partial document (C18).
 */
import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import {
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  subWeeks,
  subMonths,
} from 'date-fns';
import { db } from '../../firebase/config';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../contexts/ToastContext';
import {
  todayManilaYMD,
  manilaYMD,
  parsePeriodToDate,
  toYMDString,
  getMonthsInRange,
} from '../../utils/periodDate';
import { buildDtrModel, isEmployedInRange } from '../../services/dtrModel';
import { buildAttendanceSummaryModel, SORT_OPTIONS } from '../../services/attendanceSummaryModel';
import { loadAttendanceSummaryPdf, PDF_LOAD_ERROR } from '../../services/pdfLoader';
import { usePdfPreview } from '../../hooks/usePdfPreview';
import PdfPreviewPanel from './PdfPreviewPanel';

/** C3's window: first day of the previous Manila month → last day of the current one. */
const windowBounds = (todayYMD) => {
  const base = parsePeriodToDate(todayYMD);
  return {
    lower: toYMDString(startOfMonth(subMonths(base, 1))),
    upper: toYMDString(endOfMonth(base)),
  };
};

/**
 * C4's three blocking cases, in precedence order — empty must win over the
 * window test, because '' < lower is TRUE on a string compare, so the window
 * message would otherwise show beside an empty box. Inverted and window
 * compares are on 'YYYY-MM-DD' STRINGS. No span cap (C3).
 */
export const summaryRangeError = (fromYMD, toYMD, todayYMD) => {
  if (!fromYMD || !toYMD) return 'Select a start and end date.';
  if (toYMD < fromYMD) return 'End date cannot be before start date.';
  const { lower, upper } = windowBounds(todayYMD);
  if (fromYMD < lower || toYMD > upper) {
    return 'Date range must be within this month or last month.';
  }
  return null;
};

/**
 * C3a: the DTR's four presets (Monday-start), each END clamped to the window.
 * The clamp is load-bearing, not tidying — `This week`'s Sunday falls in the
 * NEXT month whenever today sits in a month's final partial week (2026-09-30,
 * 2026-01-31, 2026-02-26 …), which C3 rejects. The backward bound needs no
 * clamp: no preset starts before the previous month's 1st.
 */
export const buildSummaryPresets = (todayYMD) => {
  const base = parsePeriodToDate(todayYMD);
  const { upper } = windowBounds(todayYMD);
  const lastWeek = subWeeks(base, 1);
  const lastMonth = subMonths(base, 1);
  const span = (start, end) => {
    const endYMD = toYMDString(end);
    return { start: toYMDString(start), end: endYMD > upper ? upper : endYMD };
  };
  return [
    { label: 'This week', ...span(startOfWeek(base, { weekStartsOn: 1 }), endOfWeek(base, { weekStartsOn: 1 })) },
    { label: 'Last week', ...span(startOfWeek(lastWeek, { weekStartsOn: 1 }), endOfWeek(lastWeek, { weekStartsOn: 1 })) },
    { label: 'This month', ...span(startOfMonth(base), endOfMonth(base)) },
    { label: 'Last month', ...span(startOfMonth(lastMonth), endOfMonth(lastMonth)) },
  ];
};

const inputStyle = {
  padding: '0.625rem 0.75rem',
  border: '1px solid var(--ui-border)',
  borderRadius: '6px',
  fontSize: '0.875rem',
  background: 'var(--ui-surface)',
  fontFamily: 'var(--font-family-primary)',
};

const labelStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.375rem',
  fontSize: '0.75rem',
  fontWeight: 600,
  color: 'var(--ui-text-secondary)',
};

const chipStyle = (active) => ({
  padding: '0.375rem 0.75rem',
  border: `1px solid ${active ? 'var(--ui-primary)' : 'var(--ui-border)'}`,
  borderRadius: '999px',
  fontSize: '0.8125rem',
  fontWeight: 600,
  backgroundColor: active ? 'var(--ui-primary-tint)' : 'var(--ui-surface)',
  color: active ? 'var(--ui-primary)' : 'var(--ui-text-secondary)',
  cursor: 'pointer',
  fontFamily: 'var(--font-family-primary)',
});

const cardStyle = {
  padding: '1.25rem',
  background: 'var(--ui-surface)',
  border: '1px solid var(--ui-border)',
  borderRadius: '12px',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
};

const AttendanceSummaryReportPage = () => {
  const { company } = useAuth();
  const toast = useToast();

  // Default range: the current MANILA month, so the This month chip is active on load.
  const [fromYMD, setFromYMD] = useState(() => `${todayManilaYMD().slice(0, 7)}-01`);
  const [toYMD, setToYMD] = useState(() =>
    toYMDString(endOfMonth(parsePeriodToDate(todayManilaYMD())))
  );
  const [scope, setScope] = useState('all');
  // C23: the printed row order; ID ascending is the pre-C23 order.
  const [sortKey, setSortKey] = useState('employeeId');
  const [sortDirection, setSortDirection] = useState('asc');
  const [department, setDepartment] = useState('');
  const [employeeDocId, setEmployeeDocId] = useState('');
  const [employees, setEmployees] = useState([]);
  const [rosterError, setRosterError] = useState(false);
  const [generating, setGenerating] = useState(false);
  // C13/C14/C15 — the one-URL-at-a-time lifecycle and the small-screen branch,
  // shared with the DTR report so the two previews cannot drift.
  const { preview, isMobile, showPreview, clearPreview } = usePdfPreview();

  useEffect(() => {
    const fetchRoster = async () => {
      if (!company?.id) return;
      try {
        const employeesRef = collection(db, 'companies', company.id, 'employees');
        const snapshot = await getDocs(query(employeesRef, where('role', '==', 'employee')));
        const list = [];
        snapshot.forEach((d) => list.push({ id: d.id, ...d.data() }));
        list.sort((a, b) => (a.employeeId || '').localeCompare(b.employeeId || ''));
        setEmployees(list);
        setRosterError(false);
      } catch (err) {
        console.error('[AttendanceSummaryReportPage] roster read failed:', err);
        setRosterError(true);
      }
    };
    fetchRoster();
  }, [company?.id]);

  const departments = [
    ...new Set(employees.map((e) => e.employment?.department).filter(Boolean)),
  ].sort();

  // Presets and the derived validation are computed per render from the
  // Manila today — they must exist before the click that follows them (C4).
  const presets = buildSummaryPresets(todayManilaYMD());
  const dateError = summaryRangeError(fromYMD, toYMD, todayManilaYMD());

  // The filename's scope token (employeeId / department / '' for all) —
  // parallel to scopeLabel, which is the human string printed in the header.
  const scopeToken = () => {
    if (scope === 'department') return department;
    if (scope === 'single') {
      return employees.find((x) => x.id === employeeDocId)?.employeeId || '';
    }
    return '';
  };

  const scopeLabel = () => {
    if (scope === 'department') return `Department: ${department}`;
    if (scope === 'single') {
      const e = employees.find((x) => x.id === employeeDocId);
      return `Employee: ${e?.employeeId || ''} — ${e?.personalInfo?.fullName || ''}`;
    }
    return 'All employees';
  };

  const handleGenerate = async () => {
    // C4: ONE Date per click. The model gets the Date; validation gets the
    // Manila YMD derived from it — so coverage and validity cannot disagree.
    const now = new Date();
    // The Manila calendar date of `now` — the SAME derivation buildDtrModel applies to
    // the `today` Date it receives (C4). Never parsePeriodToDate a YMD back into a Date
    // for the model: that yields LOCAL midnight, the previous Manila day in an AU browser.
    const todayYMD = manilaYMD(now);
    const error = summaryRangeError(fromYMD, toYMD, todayYMD);
    if (error) {
      // A page left open across Manila midnight on the 1st lands here.
      clearPreview();
      toast.error(error);
      return;
    }

    setGenerating(true);
    try {
      let pdf;
      try {
        pdf = await loadAttendanceSummaryPdf();
      } catch (err) {
        console.error('[AttendanceSummaryReportPage] PDF chunk load failed:', err);
        clearPreview();
        toast.error(PDF_LOAD_ERROR);
        return;
      }

      // C5: batch scopes take everyone employed on ANY day of the range.
      let scoped;
      if (scope === 'single') {
        scoped = employees.filter((e) => e.id === employeeDocId);
      } else if (scope === 'department') {
        scoped = employees.filter(
          (e) => e.employment?.department === department && isEmployedInRange(e, fromYMD, toYMD)
        );
      } else {
        scoped = employees.filter((e) => isEmployedInRange(e, fromYMD, toYMD));
      }
      if (scoped.length === 0) {
        clearPreview();
        toast.error('No employees in the selected scope for that range.');
        return;
      }

      let models;
      try {
        const monthKeys = getMonthsInRange(fromYMD, toYMD);
        models = await Promise.all(
          scoped.map(async (employee) => {
            const snaps = await Promise.all(
              monthKeys.map((monthKey) =>
                getDoc(
                  doc(db, 'companies', company.id, 'dailyTimeRecords', `${monthKey}_${employee.employeeId}`)
                )
              )
            );
            // A missing document is a valid month of no records (C11/C18).
            const records = snaps.flatMap((snap) => (snap.exists() ? snap.data().timeRecords || [] : []));
            return buildDtrModel({
              company,
              employee,
              records,
              rangeStartYMD: fromYMD,
              rangeEndYMD: toYMD,
              today: now,
            });
          })
        );
      } catch (err) {
        // C18: a rejected read aborts everything — never a partial document.
        console.error('[AttendanceSummaryReportPage] record read failed:', err);
        clearPreview();
        toast.error('Failed to load time records. Please try again.');
        return;
      }

      const summary = buildAttendanceSummaryModel({
        company,
        models,
        rangeStartYMD: fromYMD,
        rangeEndYMD: toYMD,
        scopeLabel: scopeLabel(),
        sort: { key: sortKey, direction: sortDirection },
      });
      showPreview(
        pdf.buildAttendanceSummaryPdf(summary),
        pdf.summaryFileName(summary, scopeToken()),
        `${summary.header.periodLabel} · ${summary.header.scopeLabel}`
      );
    } finally {
      setGenerating(false);
    }
  };

  const generateDisabled =
    generating ||
    rosterError ||
    employees.length === 0 ||
    dateError !== null ||
    (scope === 'department' && !department) ||
    (scope === 'single' && !employeeDocId);

  return (
    <div>
      <h1 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--ui-primary)', margin: '0 0 0.25rem 0' }}>
        Attendance Summary
      </h1>
      <p style={{ fontSize: '0.875rem', color: 'var(--ui-text-secondary)', margin: '0 0 1.5rem 0' }}>
        One row per employee — days, hours, overtime, night differential and breaks — for any
        range in this month or last month.
      </p>

      {rosterError && (
        <div
          style={{
            padding: '0.75rem 1rem',
            marginBottom: '1rem',
            background: 'var(--ui-danger-tint)',
            color: 'var(--ui-danger-text)',
            borderRadius: '8px',
            fontSize: '0.875rem',
          }}
        >
          Failed to load the employee list. Reload the page and try again.
        </div>
      )}

      <div style={cardStyle}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
          {presets.map((p) => {
            const active = fromYMD === p.start && toYMD === p.end;
            return (
              <button
                key={p.label}
                type="button"
                aria-pressed={active}
                onClick={() => {
                  setFromYMD(p.start);
                  setToYMD(p.end);
                }}
                style={chipStyle(active)}
              >
                {p.label}
              </button>
            );
          })}
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '1rem' }}>
          <label style={labelStyle}>
            FROM
            <input type="date" value={fromYMD} onChange={(e) => setFromYMD(e.target.value)} style={inputStyle} />
          </label>

          <label style={labelStyle}>
            TO
            <input type="date" value={toYMD} onChange={(e) => setToYMD(e.target.value)} style={inputStyle} />
          </label>

          <label style={labelStyle}>
            SCOPE
            <select value={scope} onChange={(e) => setScope(e.target.value)} style={inputStyle}>
              <option value="all">All employees</option>
              <option value="department">By department</option>
              <option value="single">Single employee</option>
            </select>
          </label>

          {scope === 'department' && (
            <label style={labelStyle}>
              DEPARTMENT
              <select value={department} onChange={(e) => setDepartment(e.target.value)} style={inputStyle}>
                <option value="">Select a department…</option>
                {departments.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </label>
          )}

          {scope === 'single' && (
            <label style={labelStyle}>
              EMPLOYEE
              <select value={employeeDocId} onChange={(e) => setEmployeeDocId(e.target.value)} style={inputStyle}>
                <option value="">Select an employee…</option>
                {employees.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.employeeId} — {e.personalInfo?.fullName || ''}
                  </option>
                ))}
              </select>
            </label>
          )}

          <label style={labelStyle}>
            SORT BY
            <select value={sortKey} onChange={(e) => setSortKey(e.target.value)} style={inputStyle}>
              {SORT_OPTIONS.map((o) => (
                <option key={o.key} value={o.key}>{o.label}</option>
              ))}
            </select>
          </label>

          <label style={labelStyle}>
            ORDER
            <select value={sortDirection} onChange={(e) => setSortDirection(e.target.value)} style={inputStyle}>
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </select>
          </label>

          <button
            type="button"
            onClick={handleGenerate}
            disabled={generateDisabled}
            style={{
              padding: '0.625rem 1.25rem',
              background: 'var(--ui-primary)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '0.875rem',
              cursor: generateDisabled ? 'not-allowed' : 'pointer',
              opacity: generateDisabled ? 0.6 : 1,
              fontFamily: 'var(--font-family-primary)',
            }}
          >
            {generating ? 'Generating…' : 'Generate'}
          </button>
        </div>

        {dateError && (
          <p style={{ margin: '0.75rem 0 0 0', fontSize: '0.8125rem', color: 'var(--ui-danger-text)' }}>
            {dateError}
          </p>
        )}
      </div>

      <PdfPreviewPanel preview={preview} isMobile={isMobile} title="Attendance Summary preview" />
    </div>
  );
};

export default AttendanceSummaryReportPage;
